import React, { useState, useEffect } from "react";
import { ProgressBar, ProgressStep } from "@component/progress-bar";
import { JobseekerConfirmDetails } from "@component/jobseeker-confirm-details";
import { CVData } from "@type/cv";
import { processUploadedCV, updateCV } from "@service/cv";
import { getCookie } from "@util/cookie";
import { CandidateEmailOTPVerify } from "@container/candidate-email-otp-verify";
import { useQuery } from "@util/useQuery";
import { catchErrorLog, fatalErrorLog } from "@util/sentry";
import { applyForJob, getJobDetailGuest } from "@service/job";
import { Job, JobApplication } from "@type/job";
import { trackEvent } from "@util/mixpanel";
import { useCompanySubdomain } from "@context/subdomain_context";
import { useParams } from "react-router-dom";
import BasicLayout from "@container/layout/basic";
import BYOCDomainRegisterPopup from "@container/dialog/dialog-wrong-register";
import ExistingCVProfileContainer from "@container/existing-cv-profile";
import QuickApplyComplete from "@component/quick-apply-complete";
import { ResumeUploadV2 } from "@component/resume-upload-v2";
import { TriangleAlertIcon } from "lucide-react";

const JobseekerQuickApply: React.FC = () => {
    const { jobid } = useParams();
    const queryParams = useQuery();
    const [, companyInfo] = useCompanySubdomain()
    const [currentPage, setCurrentPage] = useState<string>("verify-email");
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isProcessUploadCV, setProcessUploadCV] = useState<boolean>(false);
    const [isHasErrorUpload, setHasErrorUpload] = useState<boolean>(false);
    const [jobDetail, setJobDetail] = useState<Job | undefined>(undefined);
    const [isOpenDialogBYOC, setIsOpenDialogBYOC] = useState(false);
    const [isCVExist, setIsCVExist] = useState<boolean>(false);
    const [isLoadingApply, setIsLoadingApply] = useState<boolean>(false);
    const [email, setEmail] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [jobApplication, setJobApplication] = useState<
        JobApplication | undefined
    >(undefined);

    useEffect(() => {
        const applyFormData = getCookie('__apply_job_guest__');
        if (!sessionStorage.getItem("__TEMP_CVID__") || !sessionStorage.getItem("__USERID__") || !applyFormData) {
            window.location.href = `/job/${jobid}`;
            return;
        }

        setEmail(applyFormData.email || "");
    }, [])

    useEffect(() => {
        if (jobid) {
            getJobDetailGuest(jobid).then(async (resp) => {
                setJobDetail(resp);
            })
        }
    }, [jobid])

    const handleProcessUploadCV = async () => {
        setProcessUploadCV(true);
        setHasErrorUpload(false);
        setErrorMessage("");

        const dataForm = getCookie('__apply_job_guest__');
        processUploadedCV(
            sessionStorage.getItem("__TEMP_CVID__") || "",
            sessionStorage.getItem("__USERID__") || "",
            dataForm?.name || null,
            dataForm?.country_code || null,
            dataForm?.phone || null,
            dataForm?.email || null,
        ).then((resp) => {
            if (resp.status === 200) {
                setCurrentStep(2);
                setCurrentPage("confirm-details");
            } else if (resp.status === 400 && resp.data.data.is_cv_exist) {
                setErrorMessage(resp.data?.message || "");
                setCurrentStep(2);
                setCurrentPage("user-cv-exist");
                setIsCVExist(true);
            } else {
                setCurrentStep(2);
                setCurrentPage("confirm-details");
                setErrorMessage(resp.data?.message || "");
                setHasErrorUpload(true);
            }
        }).catch((error) => {
            console.error("Error processing uploaded CV", error);
            if (error?.response?.status === 400 && error?.response?.data?.data?.is_cv_exist) {
                setErrorMessage(error?.response?.data?.message || "");
                setCurrentStep(2);
                setCurrentPage("user-cv-exist");
                setIsCVExist(true);
            } else {
                catchErrorLog(`failed to process uploaded cv, err: ${error}`);
                setHasErrorUpload(true);
            }
        }).finally(() => {
            setProcessUploadCV(false);
        })
    }

    const handleUpdateProcessNewCV = async () => {
        setProcessUploadCV(true);
        setHasErrorUpload(false);
        setErrorMessage("");
        const dataForm = getCookie('__apply_job_guest__');
        processUploadedCV(
            sessionStorage.getItem("__TEMP_CVID__") || "",
            sessionStorage.getItem("__USERID__") || "",
            dataForm?.name || null,
            dataForm?.country_code || null,
            dataForm?.phone || null,
            dataForm?.email || null,
        ).then((resp) => {
            if (resp.status === 200 || (resp.status === 400 && resp.data.data.is_existing)) {
                if (resp.status === 400 && resp.data.data.is_existing) {
                    setErrorMessage(resp.data?.message || "");
                }
                setCurrentStep(2);
                setCurrentPage("confirm-details");
            } else {
                setErrorMessage(resp.data?.message || "");
                setCurrentStep(2);
                setCurrentPage("confirm-details");
                setHasErrorUpload(true);
            }
        }).catch((error) => {
            setErrorMessage(error?.response?.data?.message || "");
                setCurrentStep(2);
            setCurrentPage("confirm-details");
            setHasErrorUpload(true);
        }).finally(() => {
            setProcessUploadCV(false);
        })
    }

    const handleProfileSave = (profile: CVData): void => {
        setIsLoading(true);

        const workExperience = profile.work_experience.sort((a, b) => {
            if (a.is_current) return -1;
            if (b.is_current) return 1;
            const yearDiff = b.period_end_year - a.period_end_year;
            if (yearDiff !== 0) return yearDiff;
            return b.period_end_month - a.period_end_month;
        });

        let workExperienceYear = 0;
        for (const experience of workExperience) {
            const startYear = experience.period_start_year;
            const endYear = experience.is_current ? new Date().getFullYear() : experience.period_end_year;
            workExperienceYear += Math.max(0, endYear - startYear);
        }

        const skillCount = profile.skills ? profile.skills.length : 0;

        // @ts-ignore
        updateCV({
            'CV_data': {
                ...profile,
                work_experience: workExperience,
            } as CVData
        })
            .then((resp) => {
                if (resp.code === 200) {
                    handleApply();
                    trackEvent('resume_details_confirmed', {
                        skill_count: skillCount,
                        work_experience_year: workExperienceYear
                    })
                    sessionStorage.removeItem("__TEMP_CVID__")
                }
            })
            .catch((error) => {
                console.error("Error updating profile", error);
                // Console-only until now. handleApply() is never reached from
                // here, so the application row is never created and the
                // candidate is left on the confirm screen believing they applied.
                fatalErrorLog(
                    "[quick-apply] profile confirmation failed — the application was never created",
                    {
                        job_id: jobid,
                        http_status: (error as { response?: { status?: number } })?.response?.status ?? "none (network or timeout)",
                        detail: String(error),
                    },
                    { area: "quick_apply" },
                );
            })

    };

    const handleApply = () => {
        setIsLoadingApply(true);
        applyForJob({
            job_id: jobid || "",
            referral_code: queryParams.get("referral") || "",
            is_quick_apply: true,
        }).then((resp) => {
            if (resp) {
                setJobApplication(resp);
                setCurrentPage("thank-you");
                setCurrentStep(3);

                sessionStorage.removeItem("__TEMP_CVID__")
                sessionStorage.removeItem("__USERID__")
            }
        }).catch((error) => {
            // The raw axios message here reads "…status code 401", which
            // catchErrorLog drops as routine auth noise — so the last step of
            // quick-apply used to fail with no record anywhere. The status goes
            // in the context instead, where nothing filters it.
            fatalErrorLog(
                "[quick-apply] apply failed — no application row was created",
                {
                    job_id: jobid,
                    http_status: (error as { response?: { status?: number } })?.response?.status ?? "none (network or timeout)",
                    detail: String(error),
                },
                { area: "quick_apply" },
            );
        }).finally(() => {
            setIsLoadingApply(false);
        });
    }

    const progressSteps: ProgressStep[] = [
        {
            label: "Apply Job",
            isActive: currentStep === 0,
            isCompleted: currentStep > 0,
        },
        {
            label: "Verify Email",
            isActive: currentStep === 1,
            isCompleted: currentStep > 1,
        },
        {
            label: "Confirm Details",
            isActive: currentStep === 2,
            isCompleted: currentStep > 2,
        },
        {
            label: "Complete",
            isActive: currentStep === 3,
            isCompleted: currentStep > 3,
        },
    ];

    return (
        <>
            <BasicLayout
                disableMinHeight
                isHideLoginButton
                showFooter={false}
                style={{
                    height: "calc(100vh - 97px)",
                }}
            >
                <div className="flex flex-col justify-center w-full">
                    {currentPage === "verify-email" && (
                        <div className="flex flex-col items-center justify-center w-full min-h-[510px] px-4">
                            <CandidateEmailOTPVerify
                                email={email}
                                onNextStep={() => {
                                    setCurrentPage("confirm-details");
                                    setCurrentStep(2);
                                    handleProcessUploadCV()
                                }}
                            />
                        </div>
                    )}

                    {(currentPage === "user-cv-exist") && (
                        <div className="flex flex-col items-center justify-center w-full min-h-[510px] px-4 py-8 mt-[4.5%] sm:mb-[96px]">
                            {isCVExist && (
                                <ExistingCVProfileContainer
                                    isLoadingApply={isLoadingApply}
                                    onContinue={(isUploadNewCV: boolean) => {
                                        setIsCVExist(false);
                                        setCurrentPage("confirm-details");
                                        setCurrentStep(2);
                                        setHasErrorUpload(false);
                                        if (isUploadNewCV) {
                                            handleUpdateProcessNewCV();
                                        }
                                    }}
                                />
                            )}
                        </div>
                    )}

                    {(currentPage === "confirm-details") && (
                        <div className="flex flex-col items-center justify-center w-full min-h-[510px] px-4 py-8 mt-[4.5%] sm:mb-[96px]">

                            {!isHasErrorUpload ? (
                                <div className="flex flex-col items-center justify-center min-h-[240px] w-full md:w-[800px] mb-10 sm:mb-0">
                                    <JobseekerConfirmDetails
                                        onSave={handleProfileSave}
                                        isLoading={isProcessUploadCV || isLoading || isLoadingApply}
                                    />
                                </div>
                            ) : (
                                    <>
                                        {errorMessage && (
                                            <div className="text-sm text-center text-red-600 font-medium mb-0">
                                                {errorMessage}
                                            </div>
                                        )}
                                        <div className="flex flex-col items-center justify-center min-h-[240px] w-full md:w-[800px]">
                                            <div className="flex flex-col items-center justify-center w-full min-h-[510px] px-4 py-8">
                                                <div className="mx-auto mt-2 md:mt-6 flex flex-col items-center">
                                                    <div className="text-left mb-8 md:mb-12 w-full">
                                                        <h1 className="text-2xl md:text-5xl font-bold text-gray-900 dark:text-white mb-2 md:mb-4 text-center flex justify-center items-center">
                                                            <TriangleAlertIcon className="w-20 h-20 text-yellow-600 pr-4" /> Oops! Something went wrong.
                                                        </h1>
                                                        <p className="text-sm md:text-xl text-gray-600 dark:text-gray-300 text-center">
                                                            We encountered an issue while processing your resume. <br />
                                                            Please try uploading your resume again.
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-center justify-center md:min-w-[800px]">
                                                    <ResumeUploadV2
                                                        onUploadComplete={() => {
                                                            setIsCVExist(false);
                                                            setCurrentPage("confirm-details");
                                                            setCurrentStep(2);
                                                            setHasErrorUpload(false);
                                                            handleUpdateProcessNewCV();
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </>
                            )}
                        </div>
                    )}

                    {(currentPage === "thank-you") && (
                        <div className="sm:flex sm:flex-col sm:items-center sm:justify-center w-full px-4 py-8 min-h-[510px] mb-16 sm:mb-0">
                            <QuickApplyComplete
                                job={jobDetail!}
                                job_application={jobApplication}
                            />
                        </div>
                    )}
                </div>
            </BasicLayout >


            {currentPage !== "login" && (
                <div className="w-full fixed bottom-0 left-0 right-0 px-4 sm:px-8 pt-4 pb-4 sm:pb-4 bg-white shadow-sm">
                    <ProgressBar
                        currentStep={currentStep + 1}
                        steps={progressSteps}
                        companyPreferences={companyInfo?.preferences}
                        isShowBanding={!!companyInfo?.preferences}
                    />
                </div>
            )}

            <BYOCDomainRegisterPopup
                isOpen={isOpenDialogBYOC}
                onClose={() => setIsOpenDialogBYOC(false)}
                userEmail={email}
            />
        </>
    );
};

export default JobseekerQuickApply;